/**
 * The navigation tree, and the shape of one item in it.
 *
 * Lifted out of Sidebar.tsx so that file exports components and nothing else.
 * Sharing a constant from a component module switches React Fast Refresh off
 * for the whole file — the sidebar stopped hot-reloading the moment
 * `buildGroups` was exported for the topbar-title test, which is precisely
 * what oxlint's react(only-export-components) rule warns about.
 *
 * titles.ts is pinned against this list by __tests__/titles.test.ts: every
 * destination here must have a name to show in the topbar.
 */
import {
  ArrowLeftRight,
  BadgePercent,
  Banknote,
  BookOpen,
  Boxes,
  Building2,
  ChartNoAxesCombined,
  ClipboardList,
  CreditCard,
  History,
  LayoutDashboard,
  type LucideIcon,
  Package,
  Receipt,
  ReceiptIndianRupee,
  RotateCcw,
  ScanBarcode,
  Settings,
  ShieldCheck,
  LifeBuoy,
  ShoppingCart,
  SlidersHorizontal,
  Tags,
  Truck,
  Undo2,
  Users,
  Wallet,
  Utensils,
} from 'lucide-react';

export type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Any one of these grants the item. Omitted = always visible. */
  requires?: string[];
  feature?: string;
  /**
   * Optional behaviour this SHOP switched on. A third, independent axis:
   * `requires` is may-this-user, `feature` is does-this-plan, `module` is
   * does-this-shop-want-it. Cosmetic only — the server gates on capability.
   */
  module?: string;
  /** Renders the unread-reply count. Only support has one. */
  badge?: 'support';
};

/**
 * Navigation, grouped as in the reference product.
 *
 * Every item declares what it needs. Items the user cannot use are NOT
 * RENDERED — not disabled. A disabled nav item the user can never enable is
 * an invitation to ask why, and support has to answer it.
 */
export function buildGroups(items: string): Array<{ label: string; items: NavItem[] }> {
  return [
  {
    label: 'Operations',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/pos', label: 'POS', icon: ScanBarcode, requires: ['pos.access'] },
      {
        to: '/tables',
        label: 'Tables',
        icon: Utensils,
        requires: ['pos.access'],
        // Only for shops that switched table service on. The industry profile
        // does that for restaurants; anyone can turn it off.
        module: 'tables',
      },
      { to: '/registers', label: 'Registers', icon: Wallet, requires: ['registers.open', 'registers.view_all'] },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/sales', label: 'Sales History', icon: Receipt, requires: ['sales.view_own', 'sales.view_all'] },
      { to: '/returns', label: 'Returns', icon: RotateCcw, requires: ['sales.return'] },
    ],
  },
  {
    label: 'Customers',
    items: [{ to: '/customers', label: 'Customers', icon: Users, requires: ['customers.view'] }],
  },
  {
    // The shop's own word. A pharmacy's nav says Medicines, a restaurant's
    // says Menu items. The ROUTE stays /products forever — it is a URL, not a
    // label, and rewriting it would break every bookmark for a noun.
    label: items,
    items: [
      { to: '/products', label: items, icon: Package, requires: ['products.view'] },
      { to: '/categories', label: 'Categories', icon: Boxes, requires: ['categories.manage'] },
      { to: '/taxes', label: 'Tax Rates', icon: BadgePercent, requires: ['taxes.manage'] },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/inventory', label: 'Stock', icon: ClipboardList, requires: ['inventory.view'] },
      {
        to: '/inventory/adjustments',
        label: 'Adjustments',
        icon: SlidersHorizontal,
        requires: ['inventory.view'],
      },
      {
        to: '/transfers',
        label: 'Transfers',
        icon: ArrowLeftRight,
        requires: ['inventory.transfer'],
        feature: 'stock_transfers',
      },
      { to: '/suppliers', label: 'Suppliers', icon: Truck, requires: ['suppliers.manage'] },
      {
        to: '/purchases',
        label: 'Purchases',
        icon: ShoppingCart,
        requires: ['purchases.view'],
        feature: 'purchases',
      },
      {
        to: '/purchase-returns',
        label: 'Purchase returns',
        icon: Undo2,
        requires: ['purchases.view'],
        feature: 'purchases',
      },
    ],
  },
  {
    /*
     * Finance — the half of the product that was built and never reachable.
     *
     * Grouped rather than scattered because these four answer one question
     * between them: where the money went. Expenses and supplier payments are
     * what a shopkeeper touches weekly; Accounting is what their CA opens once
     * a month.
     */
    label: 'Finance',
    items: [
      { to: '/expenses', label: 'Expenses', icon: ReceiptIndianRupee, requires: ['expenses.view'] },
      {
        // Reading the list needs suppliers.manage; recording a payment needs
        // supplier_payments.manage. The nav follows the READ capability.
        to: '/supplier-payments',
        label: 'Supplier payments',
        icon: Banknote,
        requires: ['suppliers.manage'],
      },
      {
        to: '/expense-categories',
        label: 'Expense categories',
        icon: Tags,
        // The index accepts either; only the writes need the manage capability.
        requires: ['expenses.view', 'expense_categories.manage'],
      },
      {
        to: '/accounting',
        label: 'Accounting',
        icon: BookOpen,
        requires: ['accounting.view'],
      },
    ],
  },
  {
    label: 'Insights',
    items: [
      {
        to: '/reports',
        label: 'Reports',
        icon: ChartNoAxesCombined,
        requires: ['reports.sales', 'reports.profit', 'reports.inventory'],
      },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/staff', label: 'Staff', icon: Users, requires: ['staff.view'] },
      { to: '/roles', label: 'Roles', icon: ShieldCheck, requires: ['roles.manage'] },
      { to: '/outlets', label: 'Outlets', icon: Building2, requires: ['outlets.manage'] },
      { to: '/settings', label: 'Settings', icon: Settings, requires: ['settings.manage'] },
      { to: '/activity-log', label: 'Activity log', icon: History, requires: ['activity.view'] },
      { to: '/billing', label: 'Billing', icon: CreditCard, requires: ['billing.manage'] },
      {
        to: '/support',
        label: 'Support',
        icon: LifeBuoy,
        requires: ['support.view', 'support.manage'],
        badge: 'support',
      },
    ],
  },
  ];
}
